/*
 * UniversalFilePreviewCard —— 通用文件富预览卡片（AI 问照片专用）
 *
 * 按文件扩展名分支渲染：
 *   - 图片 (jpg/png/webp/gif)：内联缩略图 + 点击预览大图
 *   - xlsx：委托给 aiClerk 的 FilePreviewCard（保持现有行为）
 *   - pdf：内联 <iframe> 预览
 *   - csv：解析成表格内联预览
 *   - 其他 (pptx/docx/zip 等)：仅展示文件信息，无内联预览
 *
 * 所有类型统一：顶部文件名 + 中间预览区(400px) + 底部预览/下载按钮
 * 复用 aiClerk-filePreview 的 CSS 类名保持视觉一致。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import IconVipCrown from '../../components/IconVipCrown'
import { Modal, Spin } from 'antd'
import { FilePreviewCard } from '../../aiClerk/components/blocks/FilePreviewCard'
import { downloadFile } from '../../aiClerk/components/attachments/download'
import type { FileItem } from '../../aiClerk/components/blocks/types'
import { trackAiB8112Click } from '../utils/aiPhotoJunTrack'
import './UniversalFilePreviewCard.scss'

/** 文件扩展名检测 */
function getFileExt(name: string): string {
  const match = name.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/)
  return match ? match[1].toLowerCase() : ''
}

function isImageFile(name: string): boolean {
  return /\.(jpe?g|png|webp|gif|bmp|heic|svg)(?:\?|#|$)/i.test(name)
}

function isXlsxFile(name: string): boolean {
  return /\.xlsx?(?:\?|#|$)/i.test(name)
}

function isPdfFile(name: string): boolean {
  return /\.pdf(?:\?|#|$)/i.test(name)
}

function isCsvFile(name: string): boolean {
  return /\.(?:csv|tsv)(?:\?|#|$)/i.test(name)
}

/** 文件扩展名 → 卡片左侧图标 emoji */
function fileIcon(name: string): string {
  if (isImageFile(name)) return '🖼️'
  if (isXlsxFile(name)) return '📊'
  if (isPdfFile(name)) return '📕'
  if (isCsvFile(name)) return '📊'
  const ext = getFileExt(name)
  if (ext === 'pptx' || ext === 'ppt') return '📙'
  if (ext === 'docx' || ext === 'doc') return '📄'
  return '📎'
}

/** CSV 内联预览：解析前 10 行展示 */
const CsvPreviewTable: React.FC<{ url: string }> = ({ url }) => {
  const [state, setState] = useState<{
    loading: boolean
    error: string | null
    columns: string[]
    rows: string[][]
  }>({ loading: true, error: null, columns: [], rows: [] })

  useEffect(() => {
    let disposed = false
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then((text) => {
        if (disposed) return
        const lines = text.split('\n').filter((l) => l.trim())
        if (lines.length === 0) {
          setState({ loading: false, error: '空文件', columns: [], rows: [] })
          return
        }
        const sep = lines[0].includes('\t') ? '\t' : ','
        const columns = lines[0]
          .split(sep)
          .map((c) => c.replace(/^"|"$/g, '').trim())
        const rows = lines
          .slice(1, 11)
          .map((l) => l.split(sep).map((c) => c.replace(/^"|"$/g, '').trim()))
        setState({ loading: false, error: null, columns, rows })
      })
      .catch(() => {
        if (!disposed)
          setState({
            loading: false,
            error: '预览加载失败',
            columns: [],
            rows: []
          })
      })
    return () => {
      disposed = true
    }
  }, [url])

  if (state.loading)
    return (
      <div className="aiClerk-filePreview__state">
        <Spin /> <span style={{ marginLeft: 8 }}>预览加载中...</span>
      </div>
    )
  if (state.error)
    return <div className="aiClerk-filePreview__state">{state.error}</div>
  if (state.columns.length === 0)
    return <div className="aiClerk-filePreview__state">暂无预览内容</div>

  return (
    <table className="aiClerk-filePreview__table">
      <thead>
        <tr>
          <th className="aiClerk-filePreview__idx">#</th>
          {state.columns.map((col, i) => (
            <th key={i}>{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {state.rows.map((row, rowIdx) => (
          <tr key={rowIdx}>
            <td
              className={
                'aiClerk-filePreview__idx' +
                (rowIdx < 3 ? ' aiClerk-filePreview__idx--top' : '')
              }
            >
              {rowIdx + 1}
            </td>
            {state.columns.map((_, colIdx) => (
              <td key={colIdx}>{row[colIdx] ?? ''}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** 下载任务（简化版，只需匹配 URL 用） */
interface DownloadTaskRef {
  task_id: string
  status: string
  url?: string
}

/** 神策 ai_b8112_click(点击卡片项) 埋点上下文, 与 AiPhotoJunPanel.CardTrackContext 同语义 */
interface FileCardTrackCtx {
  sessionId: string
  msgId: string
  rankNum: number
}

interface UniversalFilePreviewCardProps {
  file: FileItem
  /** xlsx 全屏预览是否按窗口铺开列宽（AI 问照片传 true） */
  fitPreviewToWindow?: boolean
  /** 下载任务列表（实时状态），用于匹配 generate_image 完成后的真实 URL */
  downloads?: DownloadTaskRef[]
  /** 下载拦截：校验会员等，返回 true 允许下载，返回 false 拦截 */
  onBeforeDownload?: (file: FileItem) => boolean
  /** 下载动作接管（可选）：传了 → 由父组件负责真正下载（如下载前扣积分）；不传 → 默认 downloadFile。
   *  仅在 onBeforeDownload 通过后才会被调用。 */
  onDownload?: (file: FileItem) => void
  /** 非会员锁定：在「立即下载」上盖会员专属蒙层（点击仍走 onBeforeDownload 跳会员页） */
  downloadLocked?: boolean
  /** 埋点上下文: 传了才上报「预览 / 立即下载」的 ai_b8112_click 点击卡片项 */
  trackCtx?: FileCardTrackCtx
}

/**
 * 卡片「预览 / 立即下载」点击 → ai_b8112_click(点击卡片项)。
 * typeName: 图片走平台枚举「图片生成卡」; xlsx/csv/pdf 等文件平台枚举暂缺,
 * 报「文件预览卡」(枚举 enable=false 不丢数, 需在平台补登记该值)。
 */
const trackFileCardAction = (
  ctx: FileCardTrackCtx | undefined,
  isImage: boolean,
  place: '预览' | '立即下载'
): void => {
  if (!ctx) return
  trackAiB8112Click({
    clickItem: '点击卡片项',
    sessionID: ctx.sessionId,
    msgID: ctx.msgId,
    rankNum: ctx.rankNum,
    typeName: isImage ? '图片生成卡' : '文件预览卡',
    place
  })
}

export const UniversalFilePreviewCard: React.FC<
  UniversalFilePreviewCardProps
> = ({
  file,
  fitPreviewToWindow,
  downloads,
  onBeforeDownload,
  onDownload,
  downloadLocked,
  trackCtx
}) => {
  // 从 downloads 里找到匹配的任务，获取最新的 URL 和状态
  const resolvedFile = useMemo(() => {
    if (!downloads || downloads.length === 0) return file
    // file.url 初始可能是 task_id（UUID），用它匹配 downloads 里的 task_id
    const match = downloads.find(
      (d) => d.task_id === file.url || d.url === file.url
    )
    if (!match || !match.url || match.url === file.url) return file
    return {
      ...file,
      url: match.url,
      desc: match.status
    }
  }, [file, downloads])

  // xlsx 委托给原始 FilePreviewCard（保持内联表格 + DocumentPreview 全屏行为不变）；
  // 非会员锁定 + 下载拦截透传过去，操作区(预览/下载)盖会员蒙层
  if (
    isXlsxFile(resolvedFile.name) ||
    (resolvedFile.preview_url && isXlsxFile(resolvedFile.preview_url))
  ) {
    return (
      <FilePreviewCard
        file={resolvedFile}
        fitPreviewToWindow={fitPreviewToWindow}
        downloadLocked={downloadLocked}
        onBeforeDownload={onBeforeDownload}
        onDownload={onDownload}
        onActionTrack={(action) =>
          trackFileCardAction(
            trackCtx,
            false,
            action === 'preview' ? '预览' : '立即下载'
          )
        }
      />
    )
  }

  return (
    <GenericPreviewCard
      file={resolvedFile}
      onBeforeDownload={onBeforeDownload}
      onDownload={onDownload}
      downloadLocked={downloadLocked}
      trackCtx={trackCtx}
    />
  )
}

/** 非 xlsx 文件的通用预览卡片 */
const GenericPreviewCard: React.FC<{
  file: FileItem
  onBeforeDownload?: (file: FileItem) => boolean
  onDownload?: (file: FileItem) => void
  downloadLocked?: boolean
  trackCtx?: FileCardTrackCtx
}> = ({ file, onBeforeDownload, onDownload, downloadLocked, trackCtx }) => {
  const [previewOpen, setPreviewOpen] = useState(false)

  // type='image' 由 previewFileFromDownload 对 generate_image 标记；也兜底扩展名检测
  const image =
    file.type === 'image' || isImageFile(file.name) || isImageFile(file.url)
  const pdf = isPdfFile(file.name) || isPdfFile(file.url)
  const csv = isCsvFile(file.name) || isCsvFile(file.url)
  // 表格(csv)预览对所有人开放(看摘要引流);图片/pdf/其他预览=暴露完整内容,非会员锁
  const previewLocked = !!downloadLocked && !csv

  const handleOpenPreview = useCallback(() => {
    // 预览锁(图片/pdf/其他):非会员点预览跳会员页;表格不锁,直接开
    if (previewLocked && onBeforeDownload && !onBeforeDownload(file)) return
    setPreviewOpen(true)
  }, [previewLocked, file, onBeforeDownload])
  const handleClosePreview = useCallback(() => setPreviewOpen(false), [])
  const handleDownload = useCallback(() => {
    if (onBeforeDownload && !onBeforeDownload(file)) return
    if (onDownload) {
      onDownload(file)
      return
    }
    downloadFile(file.url, file.name)
  }, [file, onBeforeDownload, onDownload])
  // 「会员预览 / 会员下载」按钮:走 onBeforeDownload,非会员跳会员页
  const handleGoVip = useCallback(() => {
    if (onBeforeDownload) onBeforeDownload(file)
  }, [file, onBeforeDownload])
  // desc 携带 status（'pending'/'done'/'error'），用于判断任务是否完成
  const isPending = file.desc === 'pending'
  // URL 是否有效（http 开头才是真实文件链接，否则可能是 task_id UUID）
  const hasValidUrl = /^https?:\/\//.test(file.url)
  const ext = getFileExt(file.name) || getFileExt(file.url)

  const stats = [
    file.total_rows != null ? `${file.total_rows} 条记录` : null,
    file.total_cols != null ? `${file.total_cols} 列` : null,
    file.size ? `预计 ${file.size}` : null,
    !file.size && ext ? ext.toUpperCase() : null
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="aiClerk-filePreview universal-filePreview">
      <div className="aiClerk-filePreview__head">
        <span className="aiClerk-filePreview__icon">
          {fileIcon(file.name || file.url)}
        </span>
        <span className="aiClerk-filePreview__name" title={file.name}>
          {file.name}
        </span>
        <span className="aiClerk-filePreview__tag">预览模式</span>
      </div>

      {stats && <div className="aiClerk-filePreview__stats">{stats}</div>}

      <div
        className="universal-filePreview__content"
        style={{ position: 'relative' }}
      >
        {/* 防截图水印 */}
        {!isPending && hasValidUrl && (
          <div className="universal-filePreview__watermark" />
        )}
        {isPending || !hasValidUrl ? (
          <div className="universal-filePreview__placeholder">
            <Spin />
            <span className="universal-filePreview__placeholderText">
              {image ? '图片生成中...' : '文件生成中...'}
            </span>
          </div>
        ) : image ? (
          <img
            className="universal-filePreview__image"
            src={file.url}
            alt={file.name}
            loading="lazy"
            onClick={handleOpenPreview}
          />
        ) : pdf ? (
          <iframe
            className="universal-filePreview__pdf"
            src={file.url}
            title={file.name}
          />
        ) : csv ? (
          <div className="universal-filePreview__csvWrap">
            <CsvPreviewTable url={file.url} />
          </div>
        ) : (
          <div className="universal-filePreview__placeholder">
            <span className="universal-filePreview__placeholderIcon">
              {fileIcon(file.name || file.url)}
            </span>
            <span className="universal-filePreview__placeholderText">
              {ext ? `${ext.toUpperCase()} 文件` : '文件'} · 点击预览或下载
            </span>
          </div>
        )}
      </div>

      <div className="aiClerk-filePreview__actions">
        {/* 预览:表格(csv)不锁直接看;图片/pdf/其他非会员→「预览 👑」跳会员页 */}
        <button
          type="button"
          className="aiClerk-filePreview__btn aiClerk-filePreview__btn--ghost"
          style={{ flex: 1 }}
          onClick={() => {
            trackFileCardAction(trackCtx, image, '预览')
            if (previewLocked) handleGoVip()
            else handleOpenPreview()
          }}
        >
          {previewLocked ? (
            <>
              预览 <IconVipCrown />
            </>
          ) : (
            '预览'
          )}
        </button>
        {/* 下载:会员「立即下载」;非会员→「立即下载 👑」跳会员页 */}
        <button
          type="button"
          className="aiClerk-filePreview__btn aiClerk-filePreview__btn--primary"
          style={{ flex: 1 }}
          onClick={() => {
            trackFileCardAction(trackCtx, image, '立即下载')
            if (downloadLocked) handleGoVip()
            else handleDownload()
          }}
        >
          {downloadLocked ? (
            <>
              立即下载 <IconVipCrown />
            </>
          ) : (
            '立即下载'
          )}
        </button>
      </div>

      <Modal
        open={previewOpen}
        footer={null}
        width="90%"
        title={file.name}
        onCancel={handleClosePreview}
        styles={{
          body: {
            maxHeight: '85vh',
            overflow: 'auto',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f5f5',
            position: 'relative' as const
          }
        }}
        destroyOnHidden
      >
        {/* 全屏预览水印 */}
        <div className="universal-filePreview__watermark" />
        {previewOpen && image && (
          <img
            src={file.url}
            alt={file.name}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain'
            }}
          />
        )}
        {previewOpen && pdf && (
          <iframe
            src={file.url}
            title={file.name}
            style={{ width: '100%', height: '80vh', border: 'none' }}
          />
        )}
        {previewOpen && csv && (
          <div
            style={{
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: 16
            }}
          >
            {downloadLocked && (
              <div
                role="button"
                onClick={() => {
                  // 全屏 csv 预览里的"开通会员下载完整数据"横幅 = 下载意图, 按 立即下载 上报
                  trackFileCardAction(trackCtx, image, '立即下载')
                  handleGoVip()
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  marginBottom: 12,
                  padding: '12px 16px',
                  background: '#0070f4',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                <IconVipCrown />
                开通会员，下载完整数据 →
              </div>
            )}
            <CsvPreviewTable url={file.url} />
          </div>
        )}
        {previewOpen && !image && !pdf && !csv && (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {fileIcon(file.name || file.url)}
            </div>
            <div>该文件类型暂不支持在线预览，请下载后查看</div>
            <button
              type="button"
              className="aiClerk-filePreview__btn aiClerk-filePreview__btn--primary"
              style={{ marginTop: 16 }}
              onClick={() => {
                trackFileCardAction(trackCtx, image, '立即下载')
                handleDownload()
                handleClosePreview()
              }}
            >
              立即下载
            </button>
          </div>
        )}
      </Modal>
    </div>
  )
}
